/**
 * Cup 2026 — per-generation formats, schedules, and knock-out pairing rules.
 */

export const SLOT_MINUTES = 25;
export const MATCH_MINUTES = 20;
export const CHANGEOVER_GROUP_MINUTES = 5;
export const CHANGEOVER_KO_MINUTES = 10;
/** @deprecated Use CHANGEOVER_GROUP_MINUTES */
export const CHANGEOVER_MINUTES = CHANGEOVER_GROUP_MINUTES;
export const FIRST_KICK_HOUR = 9;
export const FIRST_KICK_MINUTE = 0;
export const TOTAL_PITCHES = 6;

export const GENERATIONS = [2014, 2015, 2016, 2017, 2018, 2019] as const;
export type GenerationYear = (typeof GENERATIONS)[number];

export type MatchDayKey = 'may23' | 'may24';

export type GenerationVenue = {
  matchDay: MatchDayKey;
  /** Svi tereni koji se koriste tokom dana (za prikaz; mapiranje po slotu v. physicalPitch). */
  pitches: readonly number[];
};

export const GENERATION_VENUE: Record<GenerationYear, GenerationVenue> = {
  2014: { matchDay: 'may23', pitches: [3, 4] },
  2015: { matchDay: 'may24', pitches: [3, 4] },
  /** Tereni 1–2 u većini termina; 3–4 u kolima 3, 6, 10 i četvrtfinalima (v. physicalPitch). */
  2016: { matchDay: 'may23', pitches: [1, 2, 3, 4] },
  2017: { matchDay: 'may24', pitches: [1, 2] },
  2018: { matchDay: 'may24', pitches: [5, 6] },
  2019: { matchDay: 'may23', pitches: [5, 6] },
};

/** 2016: četiri terena u kolima 3, 6, 10 (grupa) i slotu 12 (ČF 14:20). */
export const GENERATION_2016_FOUR_PITCHES = [1, 2, 3, 4] as const;

/** 23. maj: slotovi (0-based) gde 2016 koristi terene 1–4 (10:15, 11:30, 13:10, ČF 14:20). */
export const MAY23_2016_FOUR_PITCH_SLOTS = new Set<number>([3, 6, 10, 12]);

/** 23. maj: slot 9 — samo teren 4 (C1–C4); tereni 1–2 za A. */
export const MAY23_2016_PITCH_4_ONLY_SLOTS = new Set<number>([9]);

/** @deprecated ČF su na svim terenima u slotu 12 (14:20). */
export const MAY23_2016_ON_PITCHES_34_SLOTS = new Set<number>();

/** @deprecated Koristi MAY23_2016_FOUR_PITCH_SLOTS. */
export const GENERATION_2016_FOUR_PITCH_FIRST_SLOT = 11;

export type ScheduleLane = 1 | 2 | 3 | 4;

/** Tereni prikazani u PDF / zaglavlju rasporeda. */
export function pitchesLabelForGeneration(gen: GenerationYear): string {
  return GENERATION_VENUE[gen].pitches.join(', ');
}

export function physicalPitch(gen: GenerationYear, lane: ScheduleLane, startSlot?: number): number {
  if (gen === 2016 && startSlot !== undefined && MAY23_2016_PITCH_4_ONLY_SLOTS.has(startSlot) && lane === 4) {
    return 4;
  }
  if (
    gen === 2016 &&
    startSlot !== undefined &&
    MAY23_2016_FOUR_PITCH_SLOTS.has(startSlot)
  ) {
    return GENERATION_2016_FOUR_PITCHES[lane - 1] ?? GENERATION_2016_FOUR_PITCHES[0];
  }
  const pitches = GENERATION_VENUE[gen].pitches;
  if (lane === 1 || lane === 2) return pitches[lane - 1];
  return pitches[0];
}

export type GroupId = 'A' | 'B' | 'C';

export type KnockoutFormat = 'semi_direct' | 'quarter_finals' | 'league_table' | 'league_playoffs';

export type SemiDirectPlayoffMode = 'cross_group' | 'within_group';

export type TeamsPerGroupConfig = number | Partial<Record<GroupId, number>>;

export interface GenerationFormat {
  groups: readonly GroupId[];
  teamsPerGroup: TeamsPerGroupConfig;
  knockout: KnockoutFormat;
  /** Best third-placed teams across groups that reach knock-out (quarter-finals only). */
  bestThirdsToKo: number;
  /** semi_direct: cross-group (A2–B3) vs within-group (A2–A3) play-offs. */
  semiDirectPlayoffs?: SemiDirectPlayoffMode;
  /**
   * Cross-group 3rd-place ranking: 3rd in this group is ranked by stats
   * without matches vs the team that finishes last (e.g. 5th in a 5-team group A).
   */
  thirdsExcludeLastPlaceInGroup?: GroupId;
}

/** 2014: mini liga 6 + finale (1.–2.) i meč za 3. mesto (3.–4.). 2015: 2 grupe (5+6) · baraž 2.–3., prvaci u PF. 2016–2018: 3 grupe · QF. 2019: 2×5 · baraž. */
export const GENERATION_FORMAT: Record<GenerationYear, GenerationFormat> = {
  2014: { groups: ['A'], teamsPerGroup: 6, knockout: 'league_playoffs', bestThirdsToKo: 0 },
  2015: {
    groups: ['A', 'B'],
    teamsPerGroup: { A: 5, B: 6 },
    knockout: 'semi_direct',
    bestThirdsToKo: 0,
    /** E: A2–B3 (T3); F: B2–A3 (T4). */
    semiDirectPlayoffs: 'cross_group',
  },
  2016: {
    groups: ['A', 'B', 'C'],
    teamsPerGroup: { A: 5, B: 5, C: 5 },
    knockout: 'quarter_finals',
    bestThirdsToKo: 2,
  },
  2017: {
    groups: ['A', 'B', 'C'],
    teamsPerGroup: { A: 5, B: 4, C: 4 },
    knockout: 'quarter_finals',
    bestThirdsToKo: 2,
    thirdsExcludeLastPlaceInGroup: 'A',
  },
  2018: { groups: ['A', 'B', 'C'], teamsPerGroup: 4, knockout: 'quarter_finals', bestThirdsToKo: 2 },
  2019: {
    groups: ['A', 'B'],
    teamsPerGroup: 5,
    knockout: 'semi_direct',
    bestThirdsToKo: 0,
    /** E: A2–B3 (T5); F: B2–A3 (T6) — kao 2015. */
    semiDirectPlayoffs: 'cross_group',
  },
};

export function teamsPerGroupFor(generation: GenerationYear, group: GroupId): number {
  const cfg = GENERATION_FORMAT[generation].teamsPerGroup;
  if (typeof cfg === 'number') return cfg;
  return cfg[group] ?? 4;
}

export function playoffSchematic(
  generation: GenerationYear
): Record<'po1' | 'po2', readonly [string, string]> {
  const fmt = GENERATION_FORMAT[generation];
  if (fmt.semiDirectPlayoffs === 'within_group') {
    return { po1: ['A2', 'A3'], po2: ['B2', 'B3'] };
  }
  return { po1: ['A2', 'B3'], po2: ['B2', 'A3'] };
}

export function getGenerationFormat(gen: GenerationYear): GenerationFormat {
  return GENERATION_FORMAT[gen];
}

export function groupsForGeneration(gen: GenerationYear): readonly GroupId[] {
  return GENERATION_FORMAT[gen].groups;
}

export interface GroupMatchDef {
  id: string;
  startSlot: number;
  lane: ScheduleLane;
  home: string;
  away: string;
  group: GroupId;
}

export type KoLabelKey =
  | 'po1'
  | 'po2'
  | 'qf1'
  | 'qf2'
  | 'qf3'
  | 'qf4'
  | 'sf1'
  | 'sf2'
  | 'final'
  | 'third';

export interface KoMatchDef {
  id: string;
  startSlot: number;
  lane: ScheduleLane;
  labelKey: KoLabelKey;
}

export type ScorePair = {
  home: number | null;
  away: number | null;
  /** Penali (samo nokaut); važe kad je rezultat nerešen posle regularnog vremena. */
  penHome?: number | null;
  penAway?: number | null;
};

/** ID meča u rasporedu (npr. KO-sf1, KO-fin). */
export function koMatchId(labelKey: KoLabelKey): string {
  if (labelKey === 'final') return 'KO-fin';
  if (labelKey === 'third') return 'KO-3rd';
  return `KO-${labelKey}`;
}

export function koResultKey(generation: GenerationYear, labelKey: KoLabelKey): string {
  return `${generation}-${koMatchId(labelKey)}`;
}

/** Pobednik / porazeni iz nokaut utakmice (uključujući penale posle nerešenog). */
export function koMatchOutcome(
  score: ScorePair | undefined,
  homeTeam: string,
  awayTeam: string
): { winner: string; loser: string } | null {
  if (!homeTeam.trim() || !awayTeam.trim()) return null;
  if (
    !score ||
    score.home == null ||
    score.away == null ||
    Number.isNaN(score.home) ||
    Number.isNaN(score.away)
  ) {
    return null;
  }
  if (score.home > score.away) return { winner: homeTeam, loser: awayTeam };
  if (score.away > score.home) return { winner: awayTeam, loser: homeTeam };
  const ph = score.penHome;
  const pa = score.penAway;
  if (ph == null || pa == null || Number.isNaN(ph) || Number.isNaN(pa) || ph === pa) return null;
  if (ph > pa) return { winner: homeTeam, loser: awayTeam };
  return { winner: awayTeam, loser: homeTeam };
}

function outcomeFromKoLabel(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  labelKey: KoLabelKey,
  pair: BracketPair
): { winner: string; loser: string } | null {
  return koMatchOutcome(results[koResultKey(generation, labelKey)], pair.home, pair.away);
}

export type StandingRow = {
  team: string;
  p: number;
  w: number;
  d: number;
  l: number;
  gf: number;
  ga: number;
  gd: number;
  pts: number;
};

export type BracketPair = { home: string; away: string };

const EMPTY_BRACKET_PAIR: BracketPair = { home: '', away: '' };

/**
 * Admin ručno podešeni nokaut parovi (jedna generacija). Snima se u cup-live.json.
 * Vrednosti su interni ID-jevi slotova (A1, B2, …) ili isti team stringovi kao u tabeli.
 */
export type CupKoBracketOverride =
  | { format: 'quarter_finals'; qf: BracketPair[] }
  | {
      format: 'semi_direct';
      playoffs: [BracketPair, BracketPair];
      sf: [BracketPair, BracketPair];
    };

/** Ključevi su string godina (`"2015"`) radi JSON-a. */
export type CupKoBracketOverridesRecord = Partial<Record<string, CupKoBracketOverride>>;

function isNonEmptyPair(p: BracketPair | undefined): p is BracketPair {
  return Boolean(p && String(p.home).trim() && String(p.away).trim());
}

/** Vraća undefined ako unos nije potpun — tada važi automatski proračun. */
export function sanitizeCupKoBracketOverride(
  generation: GenerationYear,
  raw: CupKoBracketOverride | null | undefined
): CupKoBracketOverride | undefined {
  if (!raw) return undefined;
  const fmt = GENERATION_FORMAT[generation].knockout;
  if (raw.format === 'quarter_finals') {
    if (fmt !== 'quarter_finals') return undefined;
    const qf = raw.qf;
    if (!Array.isArray(qf) || qf.length !== 4) return undefined;
    const norm: BracketPair[] = qf.map((p) => ({
      home: String(p?.home ?? '').trim(),
      away: String(p?.away ?? '').trim(),
    }));
    if (norm.some((p) => !p.home || !p.away)) return undefined;
    return { format: 'quarter_finals', qf: norm };
  }
  if (raw.format === 'semi_direct') {
    if (fmt !== 'semi_direct') return undefined;
    const { playoffs, sf } = raw;
    if (!playoffs?.[0] || !playoffs?.[1] || !sf?.[0] || !sf?.[1]) return undefined;
    const po: [BracketPair, BracketPair] = [
      { home: String(playoffs[0].home).trim(), away: String(playoffs[0].away).trim() },
      { home: String(playoffs[1].home).trim(), away: String(playoffs[1].away).trim() },
    ];
    const sfN: [BracketPair, BracketPair] = [
      { home: String(sf[0].home).trim(), away: String(sf[0].away).trim() },
      { home: String(sf[1].home).trim(), away: String(sf[1].away).trim() },
    ];
    if (![...po, ...sfN].every(isNonEmptyPair)) return undefined;
    return { format: 'semi_direct', playoffs: po, sf: sfN };
  }
  return undefined;
}

/**
 * Posle unetih nokaut rezultata: ažurira polufinale, finale i meč za 3. mesto
 * (pobednici/poraženi iz prethodnih rundi).
 */
export function applyKnockoutAdvancement(
  bracket: KnockoutBracket | null,
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  if (!bracket) return null;

  if (bracket.format === 'quarter_finals') {
    const wE = outcomeFromKoLabel(results, generation, 'qf1', bracket.qf[0]!);
    const wF = outcomeFromKoLabel(results, generation, 'qf2', bracket.qf[1]!);
    const wG = outcomeFromKoLabel(results, generation, 'qf3', bracket.qf[2]!);
    const wH = outcomeFromKoLabel(results, generation, 'qf4', bracket.qf[3]!);
    const sf: [BracketPair, BracketPair] = [
      {
        home: wE?.winner ?? bracket.sf[0].home,
        away: wF?.winner ?? bracket.sf[0].away,
      },
      {
        home: wG?.winner ?? bracket.sf[1].home,
        away: wH?.winner ?? bracket.sf[1].away,
      },
    ];
    const oSf1 = outcomeFromKoLabel(results, generation, 'sf1', sf[0]);
    const oSf2 = outcomeFromKoLabel(results, generation, 'sf2', sf[1]);
    const final: BracketPair = {
      home: oSf1?.winner ?? bracket.final.home,
      away: oSf2?.winner ?? bracket.final.away,
    };
    const thirdPlaceMatch: BracketPair = {
      home: oSf1?.loser ?? bracket.thirdPlaceMatch.home,
      away: oSf2?.loser ?? bracket.thirdPlaceMatch.away,
    };
    return { ...bracket, sf, final, thirdPlaceMatch };
  }

  if (bracket.format === 'semi_direct') {
    const po1 = outcomeFromKoLabel(results, generation, 'po1', bracket.playoffs[0]);
    const po2 = outcomeFromKoLabel(results, generation, 'po2', bracket.playoffs[1]);
    const sf: [BracketPair, BracketPair] = [
      {
        home: bracket.sf[0].home,
        away: po1?.winner ?? bracket.sf[0].away,
      },
      {
        home: bracket.sf[1].home,
        away: po2?.winner ?? bracket.sf[1].away,
      },
    ];
    const oSf1 = outcomeFromKoLabel(results, generation, 'sf1', sf[0]);
    const oSf2 = outcomeFromKoLabel(results, generation, 'sf2', sf[1]);
    const final: BracketPair = {
      home: oSf1?.winner ?? bracket.final.home,
      away: oSf2?.winner ?? bracket.final.away,
    };
    const thirdPlaceMatch: BracketPair = {
      home: oSf1?.loser ?? bracket.thirdPlaceMatch.home,
      away: oSf2?.loser ?? bracket.thirdPlaceMatch.away,
    };
    return { ...bracket, sf, final, thirdPlaceMatch };
  }

  return bracket;
}

/**
 * Parsiranje iz JSON-a (server / localStorage). Nepoznat format ili generacija → null.
 */
export function parseCupKoBracketOverride(
  generation: GenerationYear,
  raw: unknown
): CupKoBracketOverride | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.format === 'quarter_finals') {
    const qf = o.qf;
    if (!Array.isArray(qf)) return null;
    const pairs: BracketPair[] = [];
    for (const item of qf) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
      const p = item as Record<string, unknown>;
      pairs.push({
        home: typeof p.home === 'string' ? p.home : String(p.home ?? ''),
        away: typeof p.away === 'string' ? p.away : String(p.away ?? ''),
      });
    }
    return { format: 'quarter_finals', qf: pairs };
  }
  if (o.format === 'semi_direct') {
    const parsePair = (x: unknown): BracketPair | null => {
      if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
      const p = x as Record<string, unknown>;
      return {
        home: typeof p.home === 'string' ? p.home : String(p.home ?? ''),
        away: typeof p.away === 'string' ? p.away : String(p.away ?? ''),
      };
    };
    const pl = o.playoffs;
    const sf = o.sf;
    if (!Array.isArray(pl) || pl.length !== 2 || !Array.isArray(sf) || sf.length !== 2) return null;
    const p0 = parsePair(pl[0]);
    const p1 = parsePair(pl[1]);
    const s0 = parsePair(sf[0]);
    const s1 = parsePair(sf[1]);
    if (!p0 || !p1 || !s0 || !s1) return null;
    return { format: 'semi_direct', playoffs: [p0, p1], sf: [s0, s1] };
  }
  return null;
}

/** Kombinuje automatsku šemu i opcioni admin override. */
export function applyKoBracketOverride(
  computed: KnockoutBracket | null,
  generation: GenerationYear,
  rawOverride: CupKoBracketOverride | null | undefined
): KnockoutBracket | null {
  const override = sanitizeCupKoBracketOverride(generation, rawOverride ?? undefined);
  if (!override) return computed;

  if (override.format === 'quarter_finals') {
    if (computed?.format === 'quarter_finals') {
      return { ...computed, qf: override.qf };
    }
    return {
      format: 'quarter_finals',
      qf: override.qf,
      sf: [EMPTY_BRACKET_PAIR, EMPTY_BRACKET_PAIR],
      final: EMPTY_BRACKET_PAIR,
      thirdPlaceMatch: EMPTY_BRACKET_PAIR,
      thirdsRanked: [],
      seeds: [],
    };
  }

  return {
    format: 'semi_direct',
    playoffs: override.playoffs,
    sf: override.sf,
    final: computed?.format === 'semi_direct' ? computed.final : EMPTY_BRACKET_PAIR,
    thirdPlaceMatch:
      computed?.format === 'semi_direct' ? computed.thirdPlaceMatch : EMPTY_BRACKET_PAIR,
  };
}

/** Za snimanje: ostaju samo potpuno validni override-i po generaciji. */
export function sanitizeKoOverridesRecord(draft: CupKoBracketOverridesRecord): CupKoBracketOverridesRecord {
  const out: CupKoBracketOverridesRecord = {};
  for (const y of GENERATIONS) {
    const key = String(y);
    const raw = draft[key];
    if (!raw) continue;
    const s = sanitizeCupKoBracketOverride(y, raw);
    if (s) out[key] = s;
  }
  return out;
}

/** Custom display names keyed by `2016-A1`, etc. Internal match logic still uses A1, B2, … */
export type TeamNamesMap = Record<string, string>;

export function teamStorageKey(generation: GenerationYear, teamId: string): string {
  return `${generation}-${teamId}`;
}

export function displayTeamName(
  teamId: string,
  names: TeamNamesMap,
  generation: GenerationYear
): string {
  const custom = names[teamStorageKey(generation, teamId)]?.trim();
  return custom || teamId;
}

export function formatPairLabel(
  homeId: string,
  awayId: string,
  names: TeamNamesMap,
  generation: GenerationYear,
  separator = ' – '
): string {
  return `${displayTeamName(homeId, names, generation)}${separator}${displayTeamName(awayId, names, generation)}`;
}

/** @deprecated Use playoffSchematic(generation) for generation-specific play-off pairings. */
export const PLAYOFF_SCHEMATIC: Record<'po1' | 'po2', readonly [string, string]> = {
  po1: ['A2', 'B3'],
  po2: ['B2', 'A3'],
};

export const QF_SCHEMATIC: Record<'qf1' | 'qf2' | 'qf3' | 'qf4', readonly [string, string]> = {
  qf1: ['A1', 'III1'],
  qf2: ['B2', 'C2'],
  qf3: ['III2', 'B1'],
  qf4: ['C1', 'A2'],
};

export function isSingleGroupComplete(
  group: GroupId,
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): boolean {
  for (const m of matchesForGroup(group, generation)) {
    const sc = results[`${generation}-${m.id}`];
    if (
      sc == null ||
      sc.home == null ||
      sc.away == null ||
      Number.isNaN(sc.home) ||
      Number.isNaN(sc.away)
    ) {
      return false;
    }
  }
  return true;
}

export type SchematicResolveContext = {
  results: Record<string, ScorePair | undefined>;
  names: TeamNamesMap;
  generation: GenerationYear;
  labels: {
    roleWinner: (group: string) => string;
    roleRunnerUp: (group: string) => string;
    roleThirdInGroup: (group: string) => string;
    roleThirdRank: (rank: number) => string;
    rolePlayoffWinner: (letter: string) => string;
  };
};

/** Knock-out schematic slot: generic role until group / ranking is final, then real team name. */
export function resolveSchematicTeam(code: string, ctx: SchematicResolveContext): string {
  const { results, names, generation, labels } = ctx;

  const mIII = code.match(/^III(\d+)$/i);
  if (mIII) {
    const rank = parseInt(mIII[1], 10);
    if (isGroupStageComplete(results, generation)) {
      const thirds = thirdPlacedTeams(results, generation);
      const row = thirds[rank - 1];
      if (row) return displayTeamName(row.team, names, generation);
    }
    return labels.roleThirdRank(rank);
  }

  const mGA = code.match(/^([ABC])([123])$/);
  if (mGA) {
    const group = mGA[1] as GroupId;
    const pos = parseInt(mGA[2], 10) - 1;
    if (isSingleGroupComplete(group, results, generation)) {
      const table = buildStandings(group, results, generation);
      if (table[pos]) return displayTeamName(table[pos].team, names, generation);
    }
    if (pos === 0) return labels.roleWinner(group);
    if (pos === 1) return labels.roleRunnerUp(group);
    return labels.roleThirdInGroup(group);
  }

  return code;
}

export function formatSchematicPair(
  homeCode: string,
  awayCode: string,
  ctx: SchematicResolveContext,
  vs = ' vs '
): string {
  return `${resolveSchematicTeam(homeCode, ctx)}${vs}${resolveSchematicTeam(awayCode, ctx)}`;
}

export type KnockoutBracket =
  | {
      format: 'semi_direct';
      playoffs: [BracketPair, BracketPair];
      sf: [BracketPair, BracketPair];
      final: BracketPair;
      thirdPlaceMatch: BracketPair;
    }
  | {
      format: 'quarter_finals';
      qf: BracketPair[];
      sf: [BracketPair, BracketPair];
      final: BracketPair;
      thirdPlaceMatch: BracketPair;
      thirdsRanked: Array<{ group: GroupId; team: string; row: StandingRow }>;
      seeds: TeamMeta[];
    }
  | {
      format: 'league_table';
      champion: string;
      runnerUp: string;
      thirdPlace: string;
    }
  | {
      format: 'league_playoffs';
      final: BracketPair;
      thirdPlaceMatch: BracketPair;
    };

type TeamMeta = {
  team: string;
  group: GroupId;
  pts: number;
  gd: number;
  gf: number;
};

/** Round-robin pairings (1-indexed team numbers within a group). */
function roundRobinPairings(teamCount: number): Array<[number, number]> {
  return roundRobinRounds(teamCount).flat();
}

/** Kolo-po-kolo (manji razmaci između mečeva iste ekipe). */
function roundRobinRounds(teamCount: number): Array<Array<[number, number]>> {
  if (teamCount < 2) return [];
  const n = teamCount % 2 === 0 ? teamCount : teamCount + 1;
  const list = Array.from({ length: n }, (_, i) => i + 1);
  if (teamCount % 2 !== 0) list[n - 1] = -1; // bye
  const rounds: Array<Array<[number, number]>> = [];
  for (let r = 0; r < n - 1; r += 1) {
    const round: Array<[number, number]> = [];
    for (let i = 0; i < n / 2; i += 1) {
      const a = list[i];
      const b = list[n - 1 - i];
      if (a > 0 && b > 0) round.push([a, b]);
    }
    rounds.push(round);
    const fixed = list[0];
    const rest = list.slice(1);
    rest.unshift(rest.pop()!);
    list.splice(0, list.length, fixed, ...rest);
  }
  return rounds;
}

function groupRoundRobinMatches(group: GroupId, teamCount: number): Array<{ home: string; away: string }> {
  return roundRobinPairings(teamCount).map(([h, a]) => ({
    home: `${group}${h}`,
    away: `${group}${a}`,
  }));
}

/** Three groups: rotate which two play in parallel (same pattern as legacy template). */
function scheduleThreeGroups(
  gen: GenerationYear,
  matchesByGroup: Record<'A' | 'B' | 'C', Array<{ home: string; away: string }>>
): GroupMatchDef[] {
  const order: GroupId[] = ['A', 'B', 'C'];
  const queues = { A: [...matchesByGroup.A], B: [...matchesByGroup.B], C: [...matchesByGroup.C] };
  const out: GroupMatchDef[] = [];
  let slot = 0;
  let rot = 0;
  while (queues.A.length || queues.B.length || queues.C.length) {
    const g1 = order[rot % 3];
    const g2 = order[(rot + 1) % 3];
    const m1 = queues[g1].shift();
    const m2 = queues[g2].shift();
    if (m1) {
      const n = out.filter((x) => x.group === g1).length + 1;
      out.push({
        id: `${g1}-m${String(n).padStart(2, '0')}`,
        startSlot: slot,
        lane: 1,
        home: m1.home,
        away: m1.away,
        group: g1,
      });
    }
    if (m2) {
      const n = out.filter((x) => x.group === g2).length + 1;
      out.push({
        id: `${g2}-m${String(n).padStart(2, '0')}`,
        startSlot: slot,
        lane: 2,
        home: m2.home,
        away: m2.away,
        group: g2,
      });
    }
    if (m1 || m2) slot += 1;
    rot += 1;
    /* Ne izlazi ako su oba reda prazna u ovom paru — treća grupa može još imati mečeve (npr. A=5, B=C=4). */
  }
  return out;
}

type May23Pitch2016Match = {
  lane: ScheduleLane;
  group: GroupId;
  home: number;
  away: number;
};

type May23Pitch2014Match = {
  lane: ScheduleLane;
  home: number;
  away: number;
};

/**
 * 2014 — satnica 23. maja (lane 1 = teren 3, lane 2 = teren 4).
 * Prazni slotovi 10:15, 11:30, 13:10 — 2016 koristi terene 1–2 / 1–4.
 */
const SCHEDULE_2014_MAY23_SLOTS: ReadonlyArray<readonly May23Pitch2014Match[]> = [
  [
    { lane: 1, home: 1, away: 6 },
    { lane: 2, home: 2, away: 5 },
  ],
  [
    { lane: 1, home: 3, away: 4 },
    { lane: 2, home: 1, away: 2 },
  ],
  [
    { lane: 1, home: 3, away: 6 },
    { lane: 2, home: 4, away: 5 },
  ],
  [],
  [
    { lane: 1, home: 1, away: 5 },
    { lane: 2, home: 2, away: 6 },
  ],
  [
    { lane: 1, home: 3, away: 1 },
    { lane: 2, home: 4, away: 6 },
  ],
  [],
  [
    { lane: 1, home: 2, away: 3 },
    { lane: 2, home: 4, away: 1 },
  ],
  [
    { lane: 1, home: 5, away: 6 },
    { lane: 2, home: 2, away: 4 },
  ],
  [{ lane: 1, home: 3, away: 5 }],
  [],
];

/**
 * 23. maj — zajednički slotovi 2014 + 2016 (grupa 9:00–13:35, ČF/PF/fin 14:20–15:40).
 * 2016: lane = Teren 1–4; tereni 3–4 i u 10:15, 11:30, 12:45 (T4), 13:10.
 */
const SCHEDULE_MAY23_2014_2016: ReadonlyArray<{
  m2016: readonly May23Pitch2016Match[];
  m2014: readonly May23Pitch2014Match[];
}> = [
  {
    m2016: [
      { lane: 1, group: 'A', home: 1, away: 2 },
      { lane: 2, group: 'B', home: 1, away: 2 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[0]!],
  },
  {
    m2016: [
      { lane: 1, group: 'A', home: 3, away: 4 },
      { lane: 2, group: 'B', home: 3, away: 4 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[1]!],
  },
  {
    m2016: [
      { lane: 1, group: 'C', home: 3, away: 5 },
      { lane: 2, group: 'B', home: 1, away: 5 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[2]!],
  },
  {
    m2016: [
      { lane: 1, group: 'A', home: 2, away: 4 },
      { lane: 2, group: 'B', home: 2, away: 4 },
      { lane: 3, group: 'C', home: 1, away: 2 },
      { lane: 4, group: 'C', home: 3, away: 4 },
    ],
    m2014: [],
  },
  {
    m2016: [
      { lane: 1, group: 'A', home: 3, away: 5 },
      { lane: 2, group: 'B', home: 3, away: 5 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[4]!],
  },
  {
    m2016: [
      { lane: 1, group: 'C', home: 1, away: 5 },
      { lane: 2, group: 'C', home: 2, away: 3 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[5]!],
  },
  {
    m2016: [
      { lane: 1, group: 'C', home: 4, away: 5 },
      { lane: 2, group: 'A', home: 5, away: 2 },
      { lane: 3, group: 'B', home: 5, away: 2 },
      { lane: 4, group: 'B', home: 3, away: 1 },
    ],
    m2014: [],
  },
  {
    m2016: [
      { lane: 1, group: 'C', home: 1, away: 3 },
      { lane: 2, group: 'A', home: 1, away: 3 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[7]!],
  },
  {
    m2016: [
      { lane: 1, group: 'C', home: 4, away: 2 },
      { lane: 2, group: 'B', home: 5, away: 4 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[8]!],
  },
  {
    m2016: [
      { lane: 1, group: 'A', home: 1, away: 4 },
      { lane: 2, group: 'A', home: 2, away: 3 },
      { lane: 4, group: 'C', home: 1, away: 4 },
    ],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[9]!],
  },
  {
    m2016: [
      { lane: 1, group: 'A', home: 4, away: 5 },
      { lane: 2, group: 'B', home: 1, away: 4 },
      { lane: 3, group: 'B', home: 2, away: 3 },
      { lane: 4, group: 'C', home: 2, away: 5 },
    ],
    m2014: [],
  },
  {
    m2016: [{ lane: 1, group: 'A', home: 1, away: 5 }],
    m2014: [...SCHEDULE_2014_MAY23_SLOTS[10]!],
  },
];

function build2014GroupMatches(): GroupMatchDef[] {
  const group: GroupId = 'A';
  const out: GroupMatchDef[] = [];
  let n = 0;
  SCHEDULE_2014_MAY23_SLOTS.forEach((round, slot) => {
    for (const m of round) {
      n += 1;
      out.push({
        id: `${group}-m${String(n).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${group}${m.home}`,
        away: `${group}${m.away}`,
        group,
      });
    }
  });
  return out;
}

type ScheduleGroupPitchMatch = {
  lane: ScheduleLane;
  group: GroupId;
  home: number;
  away: number;
};

type Schedule2015PitchMatch = ScheduleGroupPitchMatch & { group: 'A' | 'B' };

/**
 * 2015 (24. maj): lane 1 = teren 3, lane 2 = teren 4; 25 min ciklus (9:00–14:00).
 */
const SCHEDULE_2015_MAY24_SLOTS: ReadonlyArray<readonly Schedule2015PitchMatch[]> = [
  [
    { lane: 1, group: 'B', home: 1, away: 2 },
    { lane: 2, group: 'B', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 5, away: 6 },
    { lane: 2, group: 'A', home: 1, away: 2 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 3 },
    { lane: 2, group: 'B', home: 2, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 3, away: 4 },
    { lane: 2, group: 'A', home: 1, away: 5 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 5 },
    { lane: 2, group: 'B', home: 2, away: 6 },
  ],
  [
    { lane: 1, group: 'A', home: 2, away: 3 },
    { lane: 2, group: 'A', home: 4, away: 5 },
  ],
  [
    { lane: 1, group: 'B', home: 5, away: 2 },
    { lane: 2, group: 'B', home: 6, away: 1 },
  ],
  [
    { lane: 1, group: 'A', home: 2, away: 5 },
    { lane: 2, group: 'A', home: 4, away: 1 },
  ],
  [
    { lane: 1, group: 'B', home: 3, away: 5 },
    { lane: 2, group: 'B', home: 4, away: 6 },
  ],
  [{ lane: 1, group: 'A', home: 3, away: 1 }],
  [
    { lane: 1, group: 'B', home: 4, away: 1 },
    { lane: 2, group: 'B', home: 6, away: 3 },
  ],
  [
    { lane: 1, group: 'A', home: 2, away: 4 },
    { lane: 2, group: 'A', home: 3, away: 5 },
  ],
  [
    { lane: 1, group: 'B', home: 3, away: 2 },
    { lane: 2, group: 'B', home: 4, away: 5 },
  ],
];

function build2015GroupMatches(): GroupMatchDef[] {
  const out: GroupMatchDef[] = [];
  const matchNo: Record<'A' | 'B', number> = { A: 0, B: 0 };
  SCHEDULE_2015_MAY24_SLOTS.forEach((round, slot) => {
    for (const m of round) {
      const g = m.group;
      matchNo[g] += 1;
      out.push({
        id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${g}${m.home}`,
        away: `${g}${m.away}`,
        group: g,
      });
    }
  });
  return out;
}

/** 2015: grupa 9:00–14:00, baraž 14:40, PF 15:10, finale 15:40. */
function build2015SlotKickoffOffsets(koLastSlot: number): number[] {
  const bySlot: Record<number, number> = {
    0: 0,
    1: 25,
    2: 50,
    3: 75,
    4: 100,
    5: 125,
    6: 150,
    7: 175,
    8: 200,
    9: 225,
    10: 250,
    11: 275,
    12: 300,
    13: 340,
    14: 370,
    15: 400,
  };
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    out.push(bySlot[s] ?? 400 + (s - 15) * 40);
  }
  return out;
}

function build2015Schedule(): GenerationSchedule {
  const groupMatches = build2015GroupMatches();
  const groupLastSlot = groupMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const koMatches = buildKoMatches(2015, groupLastSlot);
  const koLastSlot = koMatches.reduce((max, m) => Math.max(max, m.startSlot), groupLastSlot);
  const slotKickoffOffsets = build2015SlotKickoffOffsets(koLastSlot);
  return { groupMatches, koMatches, groupLastSlot, koLastSlot, slotKickoffOffsets };
}

/**
 * 2019 (24. maj): lane 1 = teren 5, lane 2 = teren 6; grupa 9:00–12:45, baraž od 13:15.
 */
const SCHEDULE_2019_MAY24_SLOTS: ReadonlyArray<readonly ScheduleGroupPitchMatch[]> = [
  [
    { lane: 1, group: 'A', home: 1, away: 2 },
    { lane: 2, group: 'A', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 2 },
    { lane: 2, group: 'B', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 1, away: 3 },
    { lane: 2, group: 'A', home: 2, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 3 },
    { lane: 2, group: 'B', home: 2, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 1, away: 5 },
    { lane: 2, group: 'A', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 5 },
    { lane: 2, group: 'B', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'A', home: 5, away: 2 },
    { lane: 2, group: 'A', home: 1, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 5, away: 2 },
    { lane: 2, group: 'B', home: 1, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 5, away: 3 },
    { lane: 2, group: 'B', home: 5, away: 3 },
  ],
  [
    { lane: 1, group: 'A', home: 5, away: 4 },
    { lane: 2, group: 'B', home: 5, away: 4 },
  ],
];

function build2019GroupMatches(): GroupMatchDef[] {
  const out: GroupMatchDef[] = [];
  const matchNo: Record<GroupId, number> = { A: 0, B: 0, C: 0 };
  SCHEDULE_2019_MAY24_SLOTS.forEach((round, slot) => {
    for (const m of round) {
      const g = m.group;
      matchNo[g] += 1;
      out.push({
        id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${g}${m.home}`,
        away: `${g}${m.away}`,
        group: g,
      });
    }
  });
  return out;
}

/** 2019: grupa +25 min; KO +30 min (10 min pauza) — baraž 13:15, PF 13:45, fin 14:15. */
function build2019SlotKickoffOffsets(koLastSlot: number): number[] {
  const groupLast = 9;
  const firstKoMin = groupLast * MAY23_GROUP_STEP_MIN + MATCH_MINUTES + 10;
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    if (s <= groupLast) {
      out.push(s * MAY23_GROUP_STEP_MIN);
    } else {
      out.push(firstKoMin + (s - groupLast - 1) * MAY24_2017_KO_STEP_MIN);
    }
  }
  return out;
}

function build2019Schedule(): GenerationSchedule {
  const groupMatches = build2019GroupMatches();
  const groupLastSlot = groupMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const koMatches = buildKoMatches(2019, groupLastSlot);
  const koLastSlot = koMatches.reduce((max, m) => Math.max(max, m.startSlot), groupLastSlot);
  const slotKickoffOffsets = build2019SlotKickoffOffsets(koLastSlot);
  return { groupMatches, koMatches, groupLastSlot, koLastSlot, slotKickoffOffsets };
}

function buildGroupMatches(gen: GenerationYear): GroupMatchDef[] {
  const fmt = GENERATION_FORMAT[gen];
  if (gen === 2014) {
    return build2014GroupMatches();
  }
  if (gen === 2015) {
    return build2015GroupMatches();
  }
  if (fmt.groups.length === 1) {
    const g = fmt.groups[0]!;
    return scheduleGroupsMinimizeTeamGaps(gen, () => 2, [g], { maxConsecutiveSlots: 2 });
  }
  if (fmt.groups.length === 2) {
    return scheduleGroupsMinimizeTeamGaps(gen, () => 2, TWO_GROUP_PICK_PATTERN);
  }
  const [gA, gB, gC] = fmt.groups as ['A', 'B', 'C'];
  return scheduleThreeGroups(gen, {
    A: groupRoundRobinMatches(gA, teamsPerGroupFor(gen, gA)),
    B: groupRoundRobinMatches(gB, teamsPerGroupFor(gen, gB)),
    C: groupRoundRobinMatches(gC, teamsPerGroupFor(gen, gC)),
  });
}

/** 2016 knock-out: ČF 14:20 (4 terena), PF 15:00, finale/3. 15:40. */
function build2016KoMatches(groupLastSlot: number): KoMatchDef[] {
  const qfSlot = groupLastSlot + 1;
  const sfSlot = groupLastSlot + 2;
  const finSlot = groupLastSlot + 3;
  return [
    { id: 'KO-qf1', startSlot: qfSlot, lane: 1, labelKey: 'qf1' },
    { id: 'KO-qf2', startSlot: qfSlot, lane: 2, labelKey: 'qf2' },
    { id: 'KO-qf3', startSlot: qfSlot, lane: 3, labelKey: 'qf3' },
    { id: 'KO-qf4', startSlot: qfSlot, lane: 4, labelKey: 'qf4' },
    { id: 'KO-sf1', startSlot: sfSlot, lane: 1, labelKey: 'sf1' },
    { id: 'KO-sf2', startSlot: sfSlot, lane: 2, labelKey: 'sf2' },
    { id: 'KO-fin', startSlot: finSlot, lane: 1, labelKey: 'final' },
    { id: 'KO-3rd', startSlot: finSlot, lane: 2, labelKey: 'third' },
  ];
}

type GroupMatchDraft = { home: string; away: string; group: GroupId };

/** Prioritet grupe pri biranju sledećeg meča (A/B češće, pa C). */
const THREE_GROUP_PICK_PATTERN: readonly GroupId[] = ['A', 'B', 'A', 'B', 'C', 'A', 'C'];

const TWO_GROUP_PICK_PATTERN: readonly GroupId[] = ['A', 'B', 'A', 'B'];

type MatchDraftWithId = GroupMatchDraft & { uid: string };

type TeamPausePlan = {
  played: number;
  /** Slot kada ekipa treba da igra sledeći meč (RR redosled). */
  nextIdealSlot: number;
  /** Pauze (broj termina) između uzastopnih mečeva ove ekipe. */
  gapsAfter: number[];
};

/** Broj termina između dva meča iste ekipe (1 = jedna pauza ≈ 25 min). */
function targetGapsBetweenTeamMatches(matchCount: number): number[] {
  if (matchCount <= 1) return [];
  if (matchCount === 2) return [1];
  if (matchCount === 3) return [1, 2];
  const gaps: number[] = [];
  for (let i = 0; i < matchCount - 1; i += 1) {
    if (i === 0 || i === matchCount - 2) gaps.push(1);
    else gaps.push(3);
  }
  return gaps;
}

function teamSlotSeed(teamId: string, group: GroupId): number {
  const num = parseInt(teamId.replace(/^[A-C]/, ''), 10) || 1;
  const gOff = group === 'A' ? 0 : group === 'B' ? 1 : 2;
  return (num + gOff) % 4;
}

function initTeamPausePlan(teamId: string, group: GroupId, matchCount: number): TeamPausePlan {
  return {
    played: 0,
    nextIdealSlot: teamSlotSeed(teamId, group),
    gapsAfter: targetGapsBetweenTeamMatches(matchCount),
  };
}

function afterMatchIdealSlot(playedAt: number, plan: TeamPausePlan): number {
  const gap = plan.gapsAfter[plan.played] ?? 3;
  return playedAt + 1 + gap;
}

/** Blokira termin ako bi ekipa već igrala u prethodna `maxConsecutive` termina. */
function wouldExceedConsecutiveSlots(
  teamId: string,
  slot: number,
  playedSlots: number[],
  maxConsecutive: number
): boolean {
  if (maxConsecutive < 1 || playedSlots.length < maxConsecutive) return false;
  for (let k = 1; k <= maxConsecutive; k += 1) {
    if (!playedSlots.includes(slot - k)) return false;
  }
  return true;
}

type ScheduleGroupOptions = {
  /** Maks. uzastopnih termina (npr. 2 = zabranjene 3 utakmice zaredom). */
  maxConsecutiveSlots?: number;
  /** Sve ekipe spremne od ovog termina (npr. 0 = prvi meč u 9:00). */
  startAllTeamsAtSlot?: number;
  /** Ista pauza (broj termina) između svih mečeva ekipe. */
  uniformGap?: number;
};

/**
 * 1–3 grupe: RR red po ekipi, bez sudara iste ekipe u istom terminu, ciljane pauze.
 */
function scheduleGroupsMinimizeTeamGaps(
  gen: GenerationYear,
  capacityForSlot: (slot: number) => number,
  groupPattern: readonly GroupId[],
  options: ScheduleGroupOptions = {}
): GroupMatchDef[] {
  const maxConsecutiveSlots = options.maxConsecutiveSlots ?? 0;
  const startAllTeamsAtSlot = options.startAllTeamsAtSlot;
  const uniformGap = options.uniformGap;
  const groups = [...GENERATION_FORMAT[gen].groups];
  const drafts: MatchDraftWithId[] = [];
  const teamOrder: Record<string, string[]> = {};
  const teamPlan: Record<string, TeamPausePlan> = {};
  let seq = 0;

  for (const g of groups) {
    const n = teamsPerGroupFor(gen, g);
    for (const round of roundRobinRounds(n)) {
      for (const [h, a] of round) {
        const home = `${g}${h}`;
        const away = `${g}${a}`;
        const uid = `__${g}-${seq}`;
        seq += 1;
        drafts.push({ uid, home, away, group: g });
        if (!teamOrder[home]) teamOrder[home] = [];
        if (!teamOrder[away]) teamOrder[away] = [];
        teamOrder[home].push(uid);
        teamOrder[away].push(uid);
        if (!teamPlan[home]) {
          teamPlan[home] = initTeamPausePlan(home, g, n - 1);
          if (startAllTeamsAtSlot !== undefined) teamPlan[home]!.nextIdealSlot = startAllTeamsAtSlot;
          if (uniformGap !== undefined) {
            teamPlan[home]!.gapsAfter = Array(n - 2).fill(uniformGap);
          }
        }
        if (!teamPlan[away]) {
          teamPlan[away] = initTeamPausePlan(away, g, n - 1);
          if (startAllTeamsAtSlot !== undefined) teamPlan[away]!.nextIdealSlot = startAllTeamsAtSlot;
          if (uniformGap !== undefined) {
            teamPlan[away]!.gapsAfter = Array(n - 2).fill(uniformGap);
          }
        }
      }
    }
  }

  const remaining = new Map(drafts.map((d) => [d.uid, d]));
  const teamNext: Record<string, number> = {};
  for (const t of Object.keys(teamOrder)) teamNext[t] = 0;

  const isEligible = (m: MatchDraftWithId): boolean => {
    const hi = teamNext[m.home] ?? 0;
    const ai = teamNext[m.away] ?? 0;
    return teamOrder[m.home]?.[hi] === m.uid && teamOrder[m.away]?.[ai] === m.uid;
  };

  const isReady = (m: MatchDraftWithId, now: number): boolean =>
    teamPlan[m.home]!.nextIdealSlot <= now && teamPlan[m.away]!.nextIdealSlot <= now;

  const pickScore = (m: MatchDraftWithId, now: number): number =>
    now - teamPlan[m.home]!.nextIdealSlot + (now - teamPlan[m.away]!.nextIdealSlot);

  const pickBest = (pool: MatchDraftWithId[], now: number): MatchDraftWithId | null => {
    const ready = pool.filter((m) => isReady(m, now));
    if (!ready.length) return null;
    let best = ready[0]!;
    let bestScore = pickScore(best, now);
    for (let i = 1; i < ready.length; i += 1) {
      const c = ready[i]!;
      const sc = pickScore(c, now);
      if (sc > bestScore) {
        best = c;
        bestScore = sc;
      }
    }
    return best;
  };

  let patternIdx = 0;
  let slot = 0;
  const scheduled: Array<MatchDraftWithId & { startSlot: number; lane: ScheduleLane }> = [];
  const teamPlayedSlots: Record<string, number[]> = {};
  const maxSlots = drafts.length * 4;

  const respectsConsecutiveLimit = (m: MatchDraftWithId, at: number): boolean => {
    if (maxConsecutiveSlots < 1) return true;
    const homeHist = teamPlayedSlots[m.home] ?? [];
    const awayHist = teamPlayedSlots[m.away] ?? [];
    return (
      !wouldExceedConsecutiveSlots(m.home, at, homeHist, maxConsecutiveSlots) &&
      !wouldExceedConsecutiveSlots(m.away, at, awayHist, maxConsecutiveSlots)
    );
  };

  while (remaining.size > 0 && slot < maxSlots) {
    const currentSlot = slot;
    const cap = capacityForSlot(currentSlot);
    const usedTeams = new Set<string>();
    let placedInSlot = 0;
    for (const m of scheduled) {
      if (m.startSlot !== currentSlot) continue;
      placedInSlot += 1;
      usedTeams.add(m.home);
      usedTeams.add(m.away);
    }

    for (let lane = placedInSlot + 1; lane <= cap; lane += 1) {
      const laneN = lane as ScheduleLane;
      const eligible = Array.from(remaining.values()).filter(
        (m) =>
          isEligible(m) &&
          respectsConsecutiveLimit(m, currentSlot) &&
          isReady(m, currentSlot) &&
          !usedTeams.has(m.home) &&
          !usedTeams.has(m.away)
      );
      if (!eligible.length) break;

      let chosen: MatchDraftWithId | null = null;
      for (let k = 0; k < groupPattern.length; k += 1) {
        const g = groupPattern[(patternIdx + k) % groupPattern.length]!;
        chosen = pickBest(
          eligible.filter((m) => m.group === g),
          currentSlot
        );
        if (chosen) {
          patternIdx = (patternIdx + k + 1) % groupPattern.length;
          break;
        }
      }
      if (!chosen) chosen = pickBest(eligible, currentSlot);
      if (!chosen) break;

      scheduled.push({ ...chosen, startSlot: currentSlot, lane: laneN });
      remaining.delete(chosen.uid);
      teamNext[chosen.home] = (teamNext[chosen.home] ?? 0) + 1;
      teamNext[chosen.away] = (teamNext[chosen.away] ?? 0) + 1;

      for (const tid of [chosen.home, chosen.away] as const) {
        const plan = teamPlan[tid]!;
        plan.played += 1;
        plan.nextIdealSlot = afterMatchIdealSlot(currentSlot, plan);
        if (!teamPlayedSlots[tid]) teamPlayedSlots[tid] = [];
        teamPlayedSlots[tid].push(currentSlot);
      }

      usedTeams.add(chosen.home);
      usedTeams.add(chosen.away);
      placedInSlot += 1;
    }

    if (placedInSlot === 0) {
      const hasEligible = Array.from(remaining.values()).some(isEligible);
      const hasReady = Array.from(remaining.values()).some(
        (m) => isEligible(m) && isReady(m, currentSlot)
      );
      if (hasEligible && !hasReady) {
        for (const t of Object.keys(teamPlan)) {
          if (teamPlan[t]!.nextIdealSlot > currentSlot) teamPlan[t]!.nextIdealSlot = currentSlot;
        }
      }
    }

    slot += 1;
  }

  const matchNo: Record<string, number> = {};
  for (const g of groups) matchNo[g] = 0;

  const laneForGroup = (g: GroupId): ScheduleLane => {
    if (groups.length === 2) return g === 'B' ? 2 : 1;
    return g === 'C' ? 2 : 1;
  };

  return scheduled.map((m) => {
    const g = m.group;
    matchNo[g] = (matchNo[g] ?? 0) + 1;
    return {
      id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
      startSlot: m.startSlot,
      lane: groups.length === 2 ? laneForGroup(g) : m.lane,
      home: m.home,
      away: m.away,
      group: g,
    };
  });
}

/** 2016: fiksni raspored 23. maja (v. SCHEDULE_MAY23_2014_2016). */
function build2016GroupMatches(): GroupMatchDef[] {
  const out: GroupMatchDef[] = [];
  const matchNo: Record<GroupId, number> = { A: 0, B: 0, C: 0 };
  SCHEDULE_MAY23_2014_2016.forEach((round, slot) => {
    for (const m of round.m2016) {
      matchNo[m.group] += 1;
      const g = m.group;
      out.push({
        id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${g}${m.home}`,
        away: `${g}${m.away}`,
        group: g,
      });
    }
  });
  return out;
}

/**
 * 2017 (24. maj): lane 1 = teren 1, lane 2 = teren 2; grupa 9:00–13:10, ČF od 13:40.
 */
const SCHEDULE_2017_MAY24_SLOTS: ReadonlyArray<readonly ScheduleGroupPitchMatch[]> = [
  [
    { lane: 1, group: 'A', home: 1, away: 2 },
    { lane: 2, group: 'A', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 2 },
    { lane: 2, group: 'B', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 1, away: 5 },
    { lane: 2, group: 'A', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 4 },
    { lane: 2, group: 'B', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'C', home: 1, away: 2 },
    { lane: 2, group: 'A', home: 3, away: 5 },
  ],
  [
    { lane: 1, group: 'C', home: 2, away: 4 },
    { lane: 2, group: 'A', home: 1, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 3 },
    { lane: 2, group: 'A', home: 5, away: 2 },
  ],
  [
    { lane: 1, group: 'C', home: 1, away: 3 },
    { lane: 2, group: 'B', home: 2, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 4, away: 5 },
    { lane: 2, group: 'C', home: 1, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 1, away: 3 },
    { lane: 2, group: 'C', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'C', home: 3, away: 4 },
    { lane: 2, group: 'A', home: 2, away: 4 },
  ],
];

function build2017GroupMatches(): GroupMatchDef[] {
  const out: GroupMatchDef[] = [];
  const matchNo: Record<GroupId, number> = { A: 0, B: 0, C: 0 };
  SCHEDULE_2017_MAY24_SLOTS.forEach((round, slot) => {
    for (const m of round) {
      const g = m.group;
      matchNo[g] += 1;
      out.push({
        id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${g}${m.home}`,
        away: `${g}${m.away}`,
        group: g,
      });
    }
  });
  return out;
}

/** 2017: grupa +25 min (5 min pauza); KO +30 min (10 min pauza) — ČF 13:40, PF 14:40, fin 15:10. */
const MAY24_2017_KO_STEP_MIN = MATCH_MINUTES + 10;

function build2017SlotKickoffOffsets(koLastSlot: number): number[] {
  const groupLast = 10;
  const firstKoMin = groupLast * MAY23_GROUP_STEP_MIN + MATCH_MINUTES + 10;
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    if (s <= groupLast) {
      out.push(s * MAY23_GROUP_STEP_MIN);
    } else {
      out.push(firstKoMin + (s - groupLast - 1) * MAY24_2017_KO_STEP_MIN);
    }
  }
  return out;
}

function build2017Schedule(): GenerationSchedule {
  const groupMatches = build2017GroupMatches();
  const groupLastSlot = groupMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const koMatches = buildKoMatches(2017, groupLastSlot);
  const koLastSlot = koMatches.reduce((max, m) => Math.max(max, m.startSlot), groupLastSlot);
  const slotKickoffOffsets = build2017SlotKickoffOffsets(koLastSlot);
  return { groupMatches, koMatches, groupLastSlot, koLastSlot, slotKickoffOffsets };
}

/**
 * 2018 (24. maj): lane 1 = teren 5, lane 2 = teren 6; grupa 9:00–12:20, ČF od 13:00.
 */
const SCHEDULE_2018_MAY24_SLOTS: ReadonlyArray<readonly ScheduleGroupPitchMatch[]> = [
  [
    { lane: 1, group: 'A', home: 1, away: 2 },
    { lane: 2, group: 'A', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 2 },
    { lane: 2, group: 'B', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 2, away: 4 },
    { lane: 2, group: 'A', home: 1, away: 3 },
  ],
  [
    { lane: 1, group: 'B', home: 2, away: 4 },
    { lane: 2, group: 'B', home: 1, away: 3 },
  ],
  [
    { lane: 1, group: 'C', home: 1, away: 2 },
    { lane: 2, group: 'C', home: 3, away: 4 },
  ],
  [
    { lane: 1, group: 'A', home: 1, away: 4 },
    { lane: 2, group: 'A', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'C', home: 2, away: 4 },
    { lane: 2, group: 'C', home: 1, away: 3 },
  ],
  [
    { lane: 1, group: 'B', home: 1, away: 4 },
    { lane: 2, group: 'B', home: 2, away: 3 },
  ],
  [
    { lane: 1, group: 'C', home: 1, away: 4 },
    { lane: 2, group: 'C', home: 2, away: 3 },
  ],
];

function build2018GroupMatches(): GroupMatchDef[] {
  const out: GroupMatchDef[] = [];
  const matchNo: Record<GroupId, number> = { A: 0, B: 0, C: 0 };
  SCHEDULE_2018_MAY24_SLOTS.forEach((round, slot) => {
    for (const m of round) {
      const g = m.group;
      matchNo[g] += 1;
      out.push({
        id: `${g}-m${String(matchNo[g]).padStart(2, '0')}`,
        startSlot: slot,
        lane: m.lane,
        home: `${g}${m.home}`,
        away: `${g}${m.away}`,
        group: g,
      });
    }
  });
  return out;
}

/** 2018: grupa +25 min; KO +30 min (10 min pauza) — ČF 13:00, PF 14:00, fin 14:30. */
function build2018SlotKickoffOffsets(koLastSlot: number): number[] {
  const groupLast = 8;
  const firstKoMin = 240;
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    if (s <= groupLast) {
      out.push(s * MAY23_GROUP_STEP_MIN);
    } else {
      out.push(firstKoMin + (s - groupLast - 1) * MAY24_2017_KO_STEP_MIN);
    }
  }
  return out;
}

function build2018Schedule(): GenerationSchedule {
  const groupMatches = build2018GroupMatches();
  const groupLastSlot = groupMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const koMatches = buildKoMatches(2018, groupLastSlot);
  const koLastSlot = koMatches.reduce((max, m) => Math.max(max, m.startSlot), groupLastSlot);
  const slotKickoffOffsets = build2018SlotKickoffOffsets(koLastSlot);
  return { groupMatches, koMatches, groupLastSlot, koLastSlot, slotKickoffOffsets };
}

/** 2016: grupa 25 min (9:00–13:35), KO runde +40 min (14:20, 15:00, 15:40). */
function build2016SlotKickoffOffsets(koLastSlot: number): number[] {
  const groupBySlot: Record<number, number> = {
    0: 0,
    1: 25,
    2: 50,
    3: 75,
    4: 100,
    5: 125,
    6: 150,
    7: 175,
    8: 200,
    9: 225,
    10: 250,
    11: 275,
  };
  const koBySlot: Record<number, number> = {
    12: 320,
    13: 360,
    14: 400,
  };
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    out.push(groupBySlot[s] ?? koBySlot[s] ?? 400 + (s - 14) * 40);
  }
  return out;
}

function build2016Schedule(): GenerationSchedule {
  const groupMatches = build2016GroupMatches();
  const groupLastSlot = groupMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const koMatches = build2016KoMatches(groupLastSlot);
  const koLastSlot = koMatches.reduce((max, m) => Math.max(max, m.startSlot), 0);
  const slotKickoffOffsets = build2016SlotKickoffOffsets(koLastSlot);

  return { groupMatches, koMatches, groupLastSlot, koLastSlot, slotKickoffOffsets };
}

function buildKoMatches(gen: GenerationYear, groupLastSlot: number): KoMatchDef[] {
  if (gen === 2016) return build2016KoMatches(groupLastSlot);

  const fmt = GENERATION_FORMAT[gen];
  const base = gen === 2014 ? groupLastSlot + 2 : groupLastSlot + 1;
  if (fmt.knockout === 'league_playoffs') {
    return [
      { id: 'KO-fin', startSlot: base, lane: 1, labelKey: 'final' },
      { id: 'KO-3rd', startSlot: base, lane: 2, labelKey: 'third' },
    ];
  }
  if (fmt.knockout === 'league_table') {
    return [];
  }
  if (fmt.knockout === 'semi_direct') {
    return [
      { id: 'KO-po1', startSlot: base, lane: 1, labelKey: 'po1' },
      { id: 'KO-po2', startSlot: base, lane: 2, labelKey: 'po2' },
      { id: 'KO-sf1', startSlot: base + 1, lane: 1, labelKey: 'sf1' },
      { id: 'KO-sf2', startSlot: base + 1, lane: 2, labelKey: 'sf2' },
      { id: 'KO-fin', startSlot: base + 2, lane: 1, labelKey: 'final' },
      { id: 'KO-3rd', startSlot: base + 2, lane: 2, labelKey: 'third' },
    ];
  }
  return [
    { id: 'KO-qf1', startSlot: base, lane: 1, labelKey: 'qf1' },
    { id: 'KO-qf2', startSlot: base, lane: 2, labelKey: 'qf2' },
    { id: 'KO-qf3', startSlot: base + 1, lane: 1, labelKey: 'qf3' },
    { id: 'KO-qf4', startSlot: base + 1, lane: 2, labelKey: 'qf4' },
    { id: 'KO-sf1', startSlot: base + 2, lane: 1, labelKey: 'sf1' },
    { id: 'KO-sf2', startSlot: base + 2, lane: 2, labelKey: 'sf2' },
    { id: 'KO-fin', startSlot: base + 3, lane: 1, labelKey: 'final' },
    { id: 'KO-3rd', startSlot: base + 3, lane: 2, labelKey: 'third' },
  ];
}

export type GenerationSchedule = {
  groupMatches: GroupMatchDef[];
  koMatches: KoMatchDef[];
  groupLastSlot: number;
  koLastSlot: number;
  slotKickoffOffsets: number[];
};

const scheduleCache = new Map<GenerationYear, GenerationSchedule>();

function buildSlotKickoffOffsets(koLastSlot: number, groupLastSlot: number): number[] {
  const out: number[] = [];
  let acc = 0;
  for (let s = 0; s <= koLastSlot; s += 1) {
    out.push(acc);
    if (s < koLastSlot) {
      const step =
        s < groupLastSlot ? MATCH_MINUTES + CHANGEOVER_GROUP_MINUTES : MATCH_MINUTES + CHANGEOVER_KO_MINUTES;
      acc += step;
    }
  }
  return out;
}

const MAY23_GROUP_STEP_MIN = MATCH_MINUTES + CHANGEOVER_GROUP_MINUTES;

/** 2014: slot 11 finale 13:35; prazni slotovi 3, 6, 10 i dalje +25 min. */
function build2014SlotKickoffOffsets(koLastSlot: number): number[] {
  const out: number[] = [];
  for (let s = 0; s <= koLastSlot; s += 1) {
    out.push(s * MAY23_GROUP_STEP_MIN);
  }
  return out;
}

export function getGenerationSchedule(gen: GenerationYear): GenerationSchedule {
  const cached = scheduleCache.get(gen);
  if (cached) return cached;

  if (gen === 2016) {
    const schedule = build2016Schedule();
    scheduleCache.set(gen, schedule);
    return schedule;
  }

  if (gen === 2015) {
    const schedule = build2015Schedule();
    scheduleCache.set(gen, schedule);
    return schedule;
  }

  if (gen === 2017) {
    const schedule = build2017Schedule();
    scheduleCache.set(gen, schedule);
    return schedule;
  }

  if (gen === 2018) {
    const schedule = build2018Schedule();
    scheduleCache.set(gen, schedule);
    return schedule;
  }

  if (gen === 2019) {
    const schedule = build2019Schedule();
    scheduleCache.set(gen, schedule);
    return schedule;
  }

  const groupMatches = buildGroupMatches(gen);
  const groupLastSlot = groupMatches.reduce((m, g) => Math.max(m, g.startSlot), 0);
  const koMatches = buildKoMatches(gen, groupLastSlot);
  const koLastSlot = koMatches.reduce((m, k) => Math.max(m, k.startSlot), groupLastSlot);
  const slotKickoffOffsets = gen === 2014 ? build2014SlotKickoffOffsets(koLastSlot) : buildSlotKickoffOffsets(koLastSlot, groupLastSlot);

  const schedule: GenerationSchedule = {
    groupMatches,
    koMatches,
    groupLastSlot,
    koLastSlot,
    slotKickoffOffsets,
  };
  scheduleCache.set(gen, schedule);
  return schedule;
}

/** Početak dodele medalja: 5 minuta posle kraja poslednje utakmice (svako godište). */
export const MEDAL_CEREMONY_START_AFTER_LAST_MATCH_MIN = 5;

/** Za 2014 (mini liga): u prikazu rezervisan blok do ~20 min za ceremoniju (procenjeni završetak). */
export const MEDAL_CEREMONY_BLOCK_MINI_LEAGUE_MIN = 20;

function formatDayClockTotalMinutes(totalMin: number): string {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

/** Minuti od ponoći za kraj poslednje utakmice (isti dan kao prvi početak). */
export function lastMatchEndTotalMinutes(gen: GenerationYear): number {
  const sched = getGenerationSchedule(gen);
  const last = sched.koLastSlot;
  const off = sched.slotKickoffOffsets[last] ?? 0;
  return FIRST_KICK_HOUR * 60 + FIRST_KICK_MINUTE + off + MATCH_MINUTES;
}

export function medalCeremonyStartTotalMinutes(gen: GenerationYear): number {
  return lastMatchEndTotalMinutes(gen) + MEDAL_CEREMONY_START_AFTER_LAST_MATCH_MIN;
}

export function medalCeremonyStartClock(gen: GenerationYear): string {
  return formatDayClockTotalMinutes(medalCeremonyStartTotalMinutes(gen));
}

/** Procenjeni kraj dodele za 2014 (početak + 20 min). */
export function medalCeremonyApproxEndClock2014(): string {
  return formatDayClockTotalMinutes(medalCeremonyStartTotalMinutes(2014) + MEDAL_CEREMONY_BLOCK_MINI_LEAGUE_MIN);
}

export function slotKickoffLabel(gen: GenerationYear, startSlot: number): string {
  const { slotKickoffOffsets } = getGenerationSchedule(gen);
  const off = slotKickoffOffsets[startSlot] ?? startSlot * SLOT_MINUTES;
  const startTotalMin = FIRST_KICK_HOUR * 60 + FIRST_KICK_MINUTE + off;
  const h = Math.floor(startTotalMin / 60);
  const m = startTotalMin % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

export function slotEndLabel(gen: GenerationYear, startSlot: number): string {
  const { slotKickoffOffsets } = getGenerationSchedule(gen);
  const off = slotKickoffOffsets[startSlot] ?? startSlot * SLOT_MINUTES;
  const endTotalMin = FIRST_KICK_HOUR * 60 + FIRST_KICK_MINUTE + off + MATCH_MINUTES;
  const h = Math.floor(endTotalMin / 60);
  const m = endTotalMin % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

export function teamsInGroup(group: GroupId, generation: GenerationYear): string[] {
  const n = teamsPerGroupFor(generation, group);
  return Array.from({ length: n }, (_, i) => `${group}${i + 1}`);
}

export function matchesForGroup(group: GroupId, generation: GenerationYear): GroupMatchDef[] {
  return getGenerationSchedule(generation).groupMatches.filter((m) => m.group === group);
}

function emptyRow(team: string): StandingRow {
  return { team, p: 0, w: 0, d: 0, l: 0, gf: 0, ga: 0, gd: 0, pts: 0 };
}

function applyGroupMatchToStandings(
  map: Map<string, StandingRow>,
  home: string,
  away: string,
  score: { home: number; away: number }
) {
  const a = map.get(home)!;
  const b = map.get(away)!;
  a.p += 1;
  b.p += 1;
  a.gf += score.home;
  a.ga += score.away;
  b.gf += score.away;
  b.ga += score.home;
  if (score.home > score.away) {
    a.w += 1;
    a.pts += 3;
    b.l += 1;
  } else if (score.home < score.away) {
    b.w += 1;
    b.pts += 3;
    a.l += 1;
  } else {
    a.d += 1;
    b.d += 1;
    a.pts += 1;
    b.pts += 1;
  }
}

function sortStandingRows(rows: StandingRow[]): StandingRow[] {
  return rows.sort((x, y) => {
    if (y.pts !== x.pts) return y.pts - x.pts;
    if (y.gd !== x.gd) return y.gd - x.gd;
    if (y.gf !== x.gf) return y.gf - x.gf;
    return x.team.localeCompare(y.team);
  });
}

/** Stats for one team in a group, optionally ignoring all matches vs `excludeOpponent`. */
function standingRowForTeam(
  teamId: string,
  group: GroupId,
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  excludeOpponent?: string
): StandingRow {
  const row = emptyRow(teamId);
  for (const m of matchesForGroup(group, generation)) {
    if (m.home !== teamId && m.away !== teamId) continue;
    if (excludeOpponent && (m.home === excludeOpponent || m.away === excludeOpponent)) {
      continue;
    }
    const score = results[`${generation}-${m.id}`];
    if (
      score == null ||
      score.home == null ||
      score.away == null ||
      Number.isNaN(score.home) ||
      Number.isNaN(score.away)
    ) {
      continue;
    }
    row.p += 1;
    if (m.home === teamId) {
      row.gf += score.home;
      row.ga += score.away;
      if (score.home > score.away) {
        row.w += 1;
        row.pts += 3;
      } else if (score.home < score.away) {
        row.l += 1;
      } else {
        row.d += 1;
        row.pts += 1;
      }
    } else {
      row.gf += score.away;
      row.ga += score.home;
      if (score.away > score.home) {
        row.w += 1;
        row.pts += 3;
      } else if (score.away < score.home) {
        row.l += 1;
      } else {
        row.d += 1;
        row.pts += 1;
      }
    }
  }
  row.gd = row.gf - row.ga;
  return row;
}

export function buildStandings(
  group: GroupId,
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): StandingRow[] {
  const teams = teamsInGroup(group, generation);
  const map = new Map<string, StandingRow>();
  teams.forEach((t) => map.set(t, emptyRow(t)));

  for (const m of matchesForGroup(group, generation)) {
    const score = results[`${generation}-${m.id}`];
    if (
      score == null ||
      score.home == null ||
      score.away == null ||
      Number.isNaN(score.home) ||
      Number.isNaN(score.away)
    ) {
      continue;
    }
    applyGroupMatchToStandings(map, m.home, m.away, {
      home: score.home,
      away: score.away,
    });
  }

  map.forEach((r) => {
    r.gd = r.gf - r.ga;
  });

  return sortStandingRows(Array.from(map.values()));
}

type ThirdPlaceRankingEntry = { group: GroupId; team: string; row: StandingRow };

/**
 * Statistika trećeplasiranog za rangiranje između grupa.
 * 2017 grupa A: posle završetka grupe, bez utakmica protiv konačnog 5. mesta.
 */
function thirdPlaceRankingRow(
  thirdTeam: string,
  group: GroupId,
  table: StandingRow[],
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): StandingRow {
  const fmt = GENERATION_FORMAT[generation];
  const groupComplete = isSingleGroupComplete(group, results, generation);

  if (
    groupComplete &&
    fmt.thirdsExcludeLastPlaceInGroup === group &&
    table.length >= 5
  ) {
    const lastPlace = table[table.length - 1]!.team;
    return standingRowForTeam(thirdTeam, group, results, generation, lastPlace);
  }

  return standingRowForTeam(thirdTeam, group, results, generation);
}

function thirdPlaceRankingEntry(
  group: GroupId,
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  options: { requireGroupComplete?: boolean } = {}
): ThirdPlaceRankingEntry | null {
  const table = buildStandings(group, results, generation);
  if (table.length < 3) return null;

  if (options.requireGroupComplete && !isSingleGroupComplete(group, results, generation)) {
    return null;
  }

  const thirdTeam = table[2]!.team;
  return {
    group,
    team: thirdTeam,
    row: thirdPlaceRankingRow(thirdTeam, group, table, results, generation),
  };
}

function compareStandingRow(a: StandingRow, b: StandingRow): number {
  if (b.pts !== a.pts) return b.pts - a.pts;
  if (b.gd !== a.gd) return b.gd - a.gd;
  if (b.gf !== a.gf) return b.gf - a.gf;
  return a.team.localeCompare(b.team);
}

export function thirdPlacedTeams(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): Array<{ group: GroupId; team: string; row: StandingRow }> {
  const fmt = GENERATION_FORMAT[generation];
  const out: ThirdPlaceRankingEntry[] = [];
  for (const g of fmt.groups) {
    const entry = thirdPlaceRankingEntry(g, results, generation, { requireGroupComplete: true });
    if (entry) out.push(entry);
  }
  return out.sort((a, b) => compareStandingRow(a.row, b.row));
}

/** Živa tabela — ista logika rangiranja; uključuje grupe pre završetka (provisional). */
export function thirdPlacedTeamsProvisional(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): Array<{ group: GroupId; team: string; row: StandingRow }> {
  const fmt = GENERATION_FORMAT[generation];
  const out: ThirdPlaceRankingEntry[] = [];
  for (const g of fmt.groups) {
    const entry = thirdPlaceRankingEntry(g, results, generation);
    if (entry) out.push(entry);
  }
  return out.sort((a, b) => compareStandingRow(a.row, b.row));
}

export function isGroupStageComplete(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): boolean {
  const { groupMatches } = getGenerationSchedule(generation);
  for (const m of groupMatches) {
    const sc = results[`${generation}-${m.id}`];
    if (
      sc == null ||
      sc.home == null ||
      sc.away == null ||
      Number.isNaN(sc.home) ||
      Number.isNaN(sc.away)
    ) {
      return false;
    }
  }
  return true;
}

function compareMeta(a: TeamMeta, b: TeamMeta): number {
  if (b.pts !== a.pts) return b.pts - a.pts;
  if (b.gd !== a.gd) return b.gd - a.gd;
  if (b.gf !== a.gf) return b.gf - a.gf;
  return a.team.localeCompare(b.team);
}

function pairWinnerVsThird(w: TeamMeta, t: TeamMeta): BracketPair {
  return { home: w.team, away: t.team };
}

function pairByStrength(a: TeamMeta, b: TeamMeta): BracketPair {
  return compareMeta(a, b) >= 0 ? { home: a.team, away: b.team } : { home: b.team, away: a.team };
}

function permutedGroupsForGeneration(gen: GenerationYear): GroupId[] {
  const fmt = GENERATION_FORMAT[gen];
  const base = [...fmt.groups];
  const ix = GENERATIONS.indexOf(gen);
  const k = ix >= 0 ? ix % base.length : 0;
  return base.map((_, i) => base[(i + k) % base.length]!);
}

/**
 * Two groups: cross-group play-offs only (no 2nd vs 3rd from same group).
 * E: A2–B3 → feeds semi with B1; F: B2–A3 → feeds semi with A1 (1st never meets own 2nd before final).
 */
function computeSemiDirectBracket(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  if (!isGroupStageComplete(results, generation)) return null;
  const [gA, gB] = GENERATION_FORMAT[generation].groups as ['A', 'B'];
  const tA = buildStandings(gA, results, generation);
  const tB = buildStandings(gB, results, generation);
  if (tA.length < 3 || tB.length < 3) return null;

  const withinGroup = GENERATION_FORMAT[generation].semiDirectPlayoffs === 'within_group';
  const playoffs: [BracketPair, BracketPair] = withinGroup
    ? [
        { home: tA[1].team, away: tA[2].team },
        { home: tB[1].team, away: tB[2].team },
      ]
    : [
        { home: tA[1].team, away: tB[2].team },
        { home: tB[1].team, away: tA[2].team },
      ];
  /** PF: B1 vs pobednik E (baraž 1), A1 vs pobednik F (baraž 2). */
  const sf: [BracketPair, BracketPair] = [
    { home: tB[0].team, away: '' },
    { home: tA[0].team, away: '' },
  ];
  return {
    format: 'semi_direct',
    playoffs,
    sf,
    final: EMPTY_BRACKET_PAIR,
    thirdPlaceMatch: EMPTY_BRACKET_PAIR,
  };
}

function computeLeaguePlayoffsBracket(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  if (!isGroupStageComplete(results, generation)) return null;
  const g = GENERATION_FORMAT[generation].groups[0]!;
  const table = buildStandings(g, results, generation);
  if (table.length < 4) return null;
  return {
    format: 'league_playoffs',
    final: { home: table[0].team, away: table[1].team },
    thirdPlaceMatch: { home: table[2].team, away: table[3].team },
  };
}

function computeLeagueTableBracket(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  if (!isGroupStageComplete(results, generation)) return null;
  const g = GENERATION_FORMAT[generation].groups[0]!;
  const table = buildStandings(g, results, generation);
  if (table.length < 3) return null;
  return {
    format: 'league_table',
    champion: table[0].team,
    runnerUp: table[1].team,
    thirdPlace: table[2].team,
  };
}

/** QF halves keep same-group 1st & 2nd apart until the final; thirds only meet group winners. */
function computeQuarterFinalsBracket(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  if (!isGroupStageComplete(results, generation)) return null;

  const fmt = GENERATION_FORMAT[generation];
  const winnersBy = {} as Record<GroupId, TeamMeta>;
  const runnersBy = {} as Record<GroupId, TeamMeta>;
  for (const g of fmt.groups) {
    const t = buildStandings(g, results, generation);
    if (t.length < 3) return null;
    winnersBy[g] = { team: t[0].team, group: g, pts: t[0].pts, gd: t[0].gd, gf: t[0].gf };
    runnersBy[g] = { team: t[1].team, group: g, pts: t[1].pts, gd: t[1].gd, gf: t[1].gf };
  }

  const thirds = thirdPlacedTeams(results, generation);
  if (thirds.length < fmt.bestThirdsToKo) return null;

  const toMeta = (x: (typeof thirds)[0]): TeamMeta => ({
    team: x.team,
    group: x.group,
    pts: x.row.pts,
    gd: x.row.gd,
    gf: x.row.gf,
  });

  const T1 = toMeta(thirds[0]);
  const T2 = toMeta(thirds[1]);

  const [g0, g1, g2] = permutedGroupsForGeneration(generation) as [GroupId, GroupId, GroupId];
  const W0 = winnersBy[g0];
  const W1 = winnersBy[g1];
  const W2 = winnersBy[g2];
  const R0 = runnersBy[g0];
  const R1 = runnersBy[g1];
  const R2 = runnersBy[g2];

  const qf1 = pairWinnerVsThird(W0, T1);
  const qf2 = pairByStrength(R1, R2);
  const wCandidates = [W1, W2].filter((w) => w.group !== T2.group);
  const wForT2 =
    [...wCandidates].sort(compareMeta)[0] ?? [...[W1, W2]].sort(compareMeta)[0];
  const wRem = wForT2.group === W1.group ? W2 : W1;
  const qf3 = pairWinnerVsThird(wForT2, T2);
  const qf4 = pairByStrength(wRem, R0);
  const qf: BracketPair[] = [qf1, qf2, qf3, qf4];

  const pool: TeamMeta[] = [...Object.values(winnersBy), ...Object.values(runnersBy), T1, T2];
  pool.sort(compareMeta);
  if (pool.length !== 8) return null;

  return {
    format: 'quarter_finals',
    qf,
    sf: [EMPTY_BRACKET_PAIR, EMPTY_BRACKET_PAIR],
    final: EMPTY_BRACKET_PAIR,
    thirdPlaceMatch: EMPTY_BRACKET_PAIR,
    thirdsRanked: thirds,
    seeds: pool,
  };
}

export function computeKnockoutBracket(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear
): KnockoutBracket | null {
  const fmt = GENERATION_FORMAT[generation];
  if (fmt.knockout === 'league_playoffs') return computeLeaguePlayoffsBracket(results, generation);
  if (fmt.knockout === 'league_table') return computeLeagueTableBracket(results, generation);
  if (fmt.knockout === 'semi_direct') return computeSemiDirectBracket(results, generation);
  return computeQuarterFinalsBracket(results, generation);
}

export type KnockoutMedalists = {
  champion: string | null;
  runnerUp: string | null;
  thirdPlace: string | null;
};

const EMPTY_MEDALISTS: KnockoutMedalists = {
  champion: null,
  runnerUp: null,
  thirdPlace: null,
};

/**
 * Konačan plasman: mini liga (tabela), ili finale + meč za 3. mesto (ukl. četvrtfinale 2016–2018).
 */
export function knockoutFinalMedalists(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  bracket: KnockoutBracket | null
): KnockoutMedalists {
  if (!bracket) return EMPTY_MEDALISTS;

  if (bracket.format === 'league_table') {
    return {
      champion: bracket.champion,
      runnerUp: bracket.runnerUp,
      thirdPlace: bracket.thirdPlace,
    };
  }

  if (
    bracket.format !== 'league_playoffs' &&
    bracket.format !== 'quarter_finals' &&
    bracket.format !== 'semi_direct'
  ) {
    return EMPTY_MEDALISTS;
  }

  const fin = bracket.final;
  const tm = bracket.thirdPlaceMatch;
  if (!fin.home || !fin.away) return EMPTY_MEDALISTS;

  const oFin = koMatchOutcome(results[koResultKey(generation, 'final')], fin.home, fin.away);
  let thirdPlace: string | null = null;
  if (tm.home && tm.away) {
    const o3rd = koMatchOutcome(results[koResultKey(generation, 'third')], tm.home, tm.away);
    thirdPlace = o3rd?.winner ?? null;
  }

  return {
    champion: oFin?.winner ?? null,
    runnerUp: oFin?.loser ?? null,
    thirdPlace,
  };
}

/** @deprecated Koristi knockoutFinalMedalists. */
export function leaguePlayoffsMedalists(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  bracket: KnockoutBracket | null
): KnockoutMedalists {
  return knockoutFinalMedalists(results, generation, bracket);
}

/** Winner of a semi-direct playoff (po1 = group A, po2 = group B). */
export function playoffWinner(
  results: Record<string, ScorePair | undefined>,
  generation: GenerationYear,
  which: 'po1' | 'po2',
  /** Efektivna šema (npr. posle applyKoBracketOverride); ako je semi_direct, koriste se ti baraž parovi. */
  effectiveBracket?: KnockoutBracket | null
): string | null {
  const bracket =
    effectiveBracket?.format === 'semi_direct'
      ? effectiveBracket
      : computeSemiDirectBracket(results, generation);
  if (!bracket || bracket.format !== 'semi_direct') return null;
  const po = which === 'po1' ? bracket.playoffs[0] : bracket.playoffs[1];
  const label = which === 'po1' ? 'po1' : 'po2';
  return koMatchOutcome(results[koResultKey(generation, label)], po.home, po.away)?.winner ?? null;
}
